import { useEffect, useState } from "react";
import { apiMutateMitZusatz } from "../hooks/useApi";
import { Button, ErrorNote, Modal } from "./components";
import { money } from "./format";
import { angabenFehlenAusFehler, angabenVollstaendig, eingabeAus, RechnungsangabenAbschnitt, rechnungsangabenBody, type RechnungsEingabe } from "./RechnungsangabenAbschnitt";

/**
 * „Rechnung senden“ (Einheit E der Rechnungs-Automatik, Antoine 26.10.2026): „Der Betreiber muss aber die Möglichkeit
 * haben, manuell rückwirkend für einen Gast eine Rechnung über MAESTRO auszulösen.“
 *
 * Eine bezahlte Zahlung ohne versendete Rechnung: der Klick ist die Freigabe, die Rechnung geht sofort per E-Mail an
 * den Gast. Fehlen dem Server Angaben (422 `rechnungsangaben_fehlen`), öffnet sich DERSELBE Abschnitt wie im
 * Bezahlt-Dialog (`RechnungsangabenAbschnitt`); die Wiederholung mit den Angaben speichert sie beim Kunden und sendet.
 * Der Server antwortet ehrlich (bereits gesendet, verworfen, storniert, Probelauf, wird gerade gesendet); sein Text
 * wird wörtlich gezeigt. Beträge sind reine Anzeige: die Rechnung lautet über den Betrag aus der Datenbank.
 */

export interface RechnungSendenZahlung {
  id: string;
  amountCents: number;
}

interface Antwort {
  rechnung?: { status: string; message?: string; an?: string | null };
}

type Abschluss = { ton: "gut" | "info"; text: string };

export function RechnungSendenDialog({
  open,
  zahlung,
  kundeName,
  grund,
  vorStichtag = false,
  onClose,
  onFertig,
}: {
  open: boolean;
  zahlung: RechnungSendenZahlung | null;
  kundeName?: string | null;
  /** Warum die Rechnung noch offen ist (aus dem Verlauf), falls bekannt. */
  grund?: string | null;
  /** Bezahlt vor dem Stichtag des Sofortversands (oder ohne Stichtag): evtl. schon ausserhalb von MAESTRO abgerechnet. */
  vorStichtag?: boolean;
  onClose: () => void;
  /** Nach „Schließen“, egal wie es ausging: die Listen laden neu. */
  onFertig: () => void;
}) {
  const [angabenNoetig, setAngabenNoetig] = useState(false);
  const [eingabe, setEingabe] = useState<RechnungsEingabe>(eingabeAus(null));
  const [geprueft, setGeprueft] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<Abschluss | null>(null);

  const zahlungId = open ? (zahlung?.id ?? null) : null;
  useEffect(() => {
    if (!zahlungId) return;
    setAngabenNoetig(false);
    setEingabe(eingabeAus(null));
    setGeprueft(false);
    setLaeuft(false);
    setFehler(null);
    setErgebnis(null);
  }, [zahlungId]);

  async function senden() {
    if (!zahlung || laeuft) return;
    setGeprueft(true);
    setFehler(null);
    if (angabenNoetig && !angabenVollstaendig(eingabe)) return;
    setLaeuft(true);
    try {
      const body = angabenNoetig ? { rechnungsangaben: rechnungsangabenBody(eingabe) } : {};
      const antwort = await apiMutateMitZusatz<unknown, Antwort>(`/api/payments/${zahlung.id}/rechnung`, "POST", body);
      const r = antwort.rechnung;
      setErgebnis({ ton: r?.status === "versand_fehlgeschlagen" ? "info" : "gut", text: r?.message ?? "Die Rechnung wurde gesendet." });
    } catch (err) {
      const neu = angabenFehlenAusFehler(err);
      if (neu) {
        // Der Server braucht Angaben: derselbe Abschnitt wie im Bezahlt-Dialog, vorbelegt mit dem Stand beim Kunden.
        setEingabe(eingabeAus(neu.kunde));
        setAngabenNoetig(true);
        setGeprueft(false);
        return;
      }
      const e = err as { code?: string; message?: string };
      // Schon gesendet ist keine Panne: der Text des Servers nennt das Datum.
      if (e.code === "bereits_gesendet") setErgebnis({ ton: "info", text: e.message ?? "Die Rechnung wurde bereits gesendet." });
      else setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setLaeuft(false);
    }
  }

  if (!open || !zahlung) return null;

  if (ergebnis) {
    return (
      <Modal
        open
        onClose={onFertig}
        width="520px"
        testId="rechnung-senden-fertig"
        title="Rechnung senden"
        footer={
          <Button type="button" data-testid="rechnung-schliessen" onClick={onFertig}>
            Schließen
          </Button>
        }
      >
        <p className="text-[13.5px] text-on-background dark:text-inverse-on-surface" data-testid="rechnung-ergebnis">
          {ergebnis.text}
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      width="560px"
      testId="rechnung-senden-dialog"
      title="Rechnung senden"
      subtitle={kundeName || undefined}
      onSubmit={(e) => {
        e.preventDefault();
        void senden();
      }}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={laeuft}>
            Abbrechen
          </Button>
          <Button type="submit" data-testid="rechnung-absenden" disabled={laeuft}>
            {laeuft ? "Wird gesendet …" : "Rechnung senden"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-xl bg-surface-container-low px-3.5 py-3 text-[13px] text-on-background dark:bg-surface-variant/20 dark:text-inverse-on-surface" data-testid="rechnung-hinweis">
          Die Rechnung über <b className="font-semibold">{money(zahlung.amountCents)}</b> geht sofort per E-Mail an den Gast.
          {grund ? (
            <>
              <br />
              <span className="text-[12px] text-secondary dark:text-secondary-fixed-dim" data-testid="rechnung-grund">
                Bisher offen, weil: {grund}
              </span>
            </>
          ) : null}
        </div>
        {vorStichtag ? (
          <p className="rounded-xl border border-outline-variant/60 px-3.5 py-2.5 text-[12.5px] text-on-background dark:border-outline/30 dark:text-inverse-on-surface" data-testid="rechnung-vorsicht">
            Nur senden, wenn der Gast noch keine Rechnung außerhalb von MAESTRO bekommen hat.
          </p>
        ) : null}
        {angabenNoetig ? <RechnungsangabenAbschnitt eingabe={eingabe} onChange={setEingabe} geprueft={geprueft} testIdPrefix="rechnung" /> : null}
        {fehler ? <ErrorNote message={fehler} /> : null}
      </div>
    </Modal>
  );
}
